import uniqBy from 'lodash.uniqby'
import { getNestedElementById, getNestedOrFloatingElementById } from '@fs/zion-sls'
import { validateKeys } from '../../../../shared/services/serviceUtils'

/** 
 __    __   _______  __      .______    _______ .______          _______.
|  |  |  | |   ____||  |     |   _  \  |   ____||   _  \        /       |
|  |__|  | |  |__   |  |     |  |_)  | |  |__   |  |_)  |      |   (----`
|   __   | |   __|  |  |     |   ___/  |   __|  |      /        \   \    
|  |  |  | |  |____ |  `----.|  |      |  |____ |  |\  \----.----)   |   
|__|  |__| |_______||_______|| _|      |_______|| _| `._____|_______/     
*/

const nameFieldTypes = ['NAME_PRE', 'NAME_GN', 'NAME_SPRE', 'NAME_SURN', 'NAME_SUF', 'NAME_PATR', 'NAME_MATR']
const nameFieldTypesWithSexCode = [...nameFieldTypes, 'SEX_CODE']
const nameFieldTypesWithOtherInfo = [...nameFieldTypes, 'BIRTH_DATE', 'DEATH_DATE', 'OCCUPATION', 'PLACE']

const getEventFieldIdsWithRects = (event) => {
  const placeFields = event?.subElements?.filter((se) => se?.elementType === 'PLACE') || []
  const dateFields = event?.subElements?.filter((se) => se?.elementType === 'DATE') || []
  const typeFields = event?.subElements?.filter((se) => se?.fieldType === 'EVENT_TYPE') || []
  return placeFields
    .concat(dateFields)
    .concat(typeFields)
    .flatMap((field) => field?.subElements || field)
    .filter((se) => se?.rects)
    .flatMap((se) => se?.id)
}

const getPersonsInfoHighlights = (args) => {
  validateKeys(args, ['highlights', 'personIds', 'includeSex', 'includeAll'])
  const { highlights, personIds, includeSex = false, includeAll = false } = args
  if (highlights) {
    let personInfoNameFieldTypes = nameFieldTypes
    if (includeAll) personInfoNameFieldTypes = nameFieldTypesWithOtherInfo
    else if (includeSex) personInfoNameFieldTypes = nameFieldTypesWithSexCode

    const filteredHighlights = uniqBy(
      personIds.flatMap((personId) =>
        highlights
          .filter((highlight) => highlight.personIds.includes(personId))
          .filter((f) => personInfoNameFieldTypes.includes(f.fieldType) || f.attached)
      ),
      (hl) => hl.id
    )

    return filteredHighlights
  }
  return undefined
}

/**
 __________   ___ .______     ______   .______     .___________. _______  _______  
|   ____\  \ /  / |   _  \   /  __  \  |   _  \    |           ||   ____||       \ 
|  |__   \  V  /  |  |_)  | |  |  |  | |  |_)  |   `---|  |----`|  |__   |  .--.  |
|   __|   >   <   |   ___/  |  |  |  | |      /        |  |     |   __|  |  |  |  |
|  |____ /  .  \  |  |      |  `--'  | |  |\  \----.   |  |     |  |____ |  '--'  |
|_______/__/ \__\ | _|       \______/  | _| `._____|   |__|     |_______||_______/ 
 */

/**
 * @param {Object} args
 * @param {import('../../../utils/SlsHighlightHookContext').HighlightUtilityObject} [args.h]
 * @param {Array} args.highlights Optional, the call to setDisplayHighlights will handle the case where highlights is undefined
 */
const showAllPersonsInfoHighlights = (args) => {
  validateKeys(args, ['h', 'mainPartitionKey', 'splitScreenPartitionKey', 'personIds', 'isSmallScreen'])
  const { h, mainPartitionKey, splitScreenPartitionKey, personIds, isSmallScreen } = args
  const highlights = [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights]
  const personHighlights = uniqBy(
    personIds.flatMap((personId) =>
      highlights
        .filter((highlight) => highlight.personIds.includes(personId))
        .filter((highlight) => nameFieldTypes.includes(highlight.fieldType))
        .map((highlight) => ({ ...highlight, attached: true }))
    ),
    (highlight) => highlight.id
  )

  h.setDisplayHighlights({
    mainPartitionKey,
    splitScreenPartitionKey,
    highlights: personHighlights,
    isSmallScreen,
    attachFilter: () => true,
  })
}

/**
 *
 * Commonly associated with Step One & Step Two
 * @param {*} args
 * @param {import('../../../utils/SlsHighlightHookContext').HighlightUtilityObject} [args.h]
 */
const showEssentialInfoHighlights = (args) => {
  validateKeys(args, [
    'h',
    'mainPartitionKey',
    'splitScreenPartitionKey',
    'personIds',
    'highlights',
    'recordHighlights',
    'isSmallScreen',
  ])
  const { h, mainPartitionKey, splitScreenPartitionKey, personIds, isSmallScreen } = args
  let { highlights, recordHighlights } = args
  if (!highlights)
    highlights = [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights]
  if (!recordHighlights)
    recordHighlights = [...h.getHighlights().mainRecordHighlights, ...h.getHighlights().splitScreenRecordHighlights]

  const hl = getPersonsInfoHighlights({ highlights, personIds })
  h.setDisplayHighlights({
    mainPartitionKey,
    splitScreenPartitionKey,
    highlights: hl,
    recordHighlights,
    setMainZoom: true,
    setSplitScreenZoom: Boolean(splitScreenPartitionKey),
    isSmallScreen,
  })
}

/**
 *
 * Commonly associated with Step Three
 * @param {Object} args
 * @param {import('../../../utils/SlsHighlightHookContext').HighlightUtilityObject} [args.h]
 */
const showEventsHighlights = (args) => {
  validateKeys(args, [
    'h',
    'currentTask',
    'highlights',
    'eventIds',
    'isSmallScreen',
    'includeAll',
    'splitScreenPartitionKey',
    'forceFieldIds',
  ])
  const {
    h,
    currentTask,
    highlights,
    eventIds = [],
    isSmallScreen,
    includeAll = false,
    splitScreenPartitionKey,
    forceFieldIds,
  } = args

  const events = eventIds?.flatMap((eventId) => getNestedElementById(currentTask?.subElements, eventId))
  const fieldIds = events?.flatMap((event) => getEventFieldIdsWithRects(event))
  const personIds = events?.flatMap((event) =>
    event?.superElements
      ?.filter(({ id }) => {
        const isOnCurrentTask = Boolean(getNestedElementById([currentTask], id))
        return isOnCurrentTask
      })
      ?.map((se) => se.id)
  )

  const highlightsToShow = [
    ...getPersonsInfoHighlights({ highlights, personIds, includeAll }),
    ...highlights.filter((hl) => (forceFieldIds ? forceFieldIds.includes(hl.fieldId) : fieldIds.includes(hl.fieldId))),
  ]

  h.setDisplayHighlights({
    mainPartitionKey: currentTask.partitionKey,
    splitScreenPartitionKey,
    highlights: highlightsToShow,
    setMainZoom: true,
    setSplitScreenZoom: Boolean(splitScreenPartitionKey),
    isSmallScreen,
  })
}

/**
 *
 * Commonly associated with Step Four
 * @param {Object} args
 * @param {import('../../../utils/SlsHighlightHookContext').HighlightUtilityObject} [args.h]
 */
const showAllNamesAndEventsHighlights = (args) => {
  validateKeys(args, ['h', 'currentTask', 'highlights', 'personIds', 'isSmallScreen', 'splitScreenPartitionKey'])
  const { h, currentTask, highlights, personIds, isSmallScreen, splitScreenPartitionKey } = args
  const persons = currentTask.subElements.filter((e) => personIds.includes(e.id))
  const events = persons.flatMap((person) => person?.subElements?.filter((el) => el.elementType === 'EVENT'))
  const fieldIds = events.flatMap((event) => getEventFieldIdsWithRects(event))

  h.setDisplayHighlights({
    mainPartitionKey: currentTask.partitionKey,
    splitScreenPartitionKey,
    highlights,
    elementIds: [...personIds, ...fieldIds],
    setMainZoom: true,
    setSplitScreenZoom: Boolean(splitScreenPartitionKey),
    isSmallScreen,
  })
}

/**
 * Used to check if a highlight is shareable between two fields. Commonly used when editing or adding a person's surname.
 * @param {Array<Object>} highlights The current array of highlights
 * @param {string} fieldId The id of the field that the highlight is being added to, if working with an existing field
 * @param {Object} currentTask The current task object
 * @param {string} fieldType The field type of the field that the highlight is being added to, if working with a new field
 * @returns An updated array of highlights and the shared field if one is found
 */
const checkIfHighlightIsShareable = (highlights, fieldId, currentTask, fieldType) => {
  let sharedField = null
  const updatedHighlights = highlights.map((h) => {
    const hHasChange = h.change
    const hIsForThisField = h.fieldId === fieldId
    const hWasOnAnotherField = h.originalHighlight.fieldId && h.fieldId !== h.originalHighlight.fieldId
    if (hHasChange && hIsForThisField && hWasOnAnotherField) {
      const receivingField = getNestedOrFloatingElementById([currentTask], fieldId) || { fieldType }
      const formerFieldIsOnTask = getNestedOrFloatingElementById([currentTask], h.originalHighlight.fieldId)
      const fieldsHaveSameTypes = formerFieldIsOnTask?.fieldType === receivingField.fieldType
      if (formerFieldIsOnTask && fieldsHaveSameTypes) {
        sharedField = formerFieldIsOnTask
        // I think this is so that there is no REM_RECT/ADD_RECT dispatches for this highlight since we're just ADD_SUBing it to the newField
        const newHighlight = {
          ...h,
          fieldId: sharedField.id,
        }
        delete newHighlight.change
        return newHighlight
      }
    }
    return h
  })

  return { sharedField, updatedHighlights }
}

export {
  showEssentialInfoHighlights,
  showEventsHighlights,
  showAllNamesAndEventsHighlights,
  checkIfHighlightIsShareable,
  showAllPersonsInfoHighlights,
}
